import type { Locale } from '@/domain/settings/types';

/**
 * Nomes localizados das 63 combinações de tipos.
 * Chave = typeSignature (ordenado alfabeticamente).
 */

const PT_BR: Record<string, string> = {
  // 1 tipo (6)
  'Air': 'Ar',
  'Earth': 'Terra',
  'Fire': 'Fogo',
  'Life': 'Vida',
  'Shadow': 'Sombra',
  'Water': 'Água',
  // 2 tipos (15)
  'Air-Earth': 'Poeira',
  'Air-Fire': 'Relâmpago',
  'Air-Life': 'Pólen',
  'Air-Shadow': 'Neblina',
  'Air-Water': 'Gelo',
  'Earth-Fire': 'Magma',
  'Earth-Life': 'Floresta',
  'Earth-Shadow': 'Caverna',
  'Earth-Water': 'Lama',
  'Fire-Life': 'Fênix',
  'Fire-Shadow': 'Eclipse',
  'Fire-Water': 'Vapor',
  'Life-Shadow': 'Fantasma',
  'Life-Water': 'Pântano',
  'Shadow-Water': 'Abismo',
  // 3 tipos (20)
  'Air-Earth-Fire': 'Vulcão Celeste',
  'Air-Earth-Life': 'Jardim Flutuante',
  'Air-Earth-Shadow': 'Tempestade de Areia',
  'Air-Earth-Water': 'Dilúvio',
  'Air-Fire-Life': 'Aurora Boreal',
  'Air-Fire-Shadow': 'Fumaça Negra',
  'Air-Fire-Water': 'Ciclone',
  'Air-Life-Shadow': 'Miragem',
  'Air-Life-Water': 'Orvalho',
  'Air-Shadow-Water': 'Nevoeiro Profundo',
  'Earth-Fire-Life': 'Erupção Viva',
  'Earth-Fire-Shadow': 'Obsidiana',
  'Earth-Fire-Water': 'Gêiser',
  'Earth-Life-Shadow': 'Ruína Antiga',
  'Earth-Life-Water': 'Mangue',
  'Earth-Shadow-Water': 'Gruta Submersa',
  'Fire-Life-Shadow': 'Fogo-Fátuo',
  'Fire-Life-Water': 'Fonte Termal',
  'Fire-Shadow-Water': 'Maré Negra',
  'Life-Shadow-Water': 'Lago Sombrio',
  // 4 tipos (15)
  'Air-Earth-Fire-Life': 'Paraíso Vulcânico',
  'Air-Earth-Fire-Shadow': 'Cinzas do Vento',
  'Air-Earth-Fire-Water': 'Tempestade Primordial',
  'Air-Earth-Life-Shadow': 'Selva Fantasma',
  'Air-Earth-Life-Water': 'Oásis',
  'Air-Earth-Shadow-Water': 'Abismo Ventoso',
  'Air-Fire-Life-Shadow': 'Cometa Espectral',
  'Air-Fire-Life-Water': 'Arco-Íris',
  'Air-Fire-Shadow-Water': 'Tormenta Sombria',
  'Air-Life-Shadow-Water': 'Bruma Encantada',
  'Earth-Fire-Life-Shadow': 'Forja Ancestral',
  'Earth-Fire-Life-Water': 'Nascente Ardente',
  'Earth-Fire-Shadow-Water': 'Caldeira Profunda',
  'Earth-Life-Shadow-Water': 'Pântano Assombrado',
  'Fire-Life-Shadow-Water': 'Alquimia Pura',
  // 5 tipos (6)
  'Air-Earth-Fire-Life-Shadow': 'Caos Elemental',
  'Air-Earth-Fire-Life-Water': 'Gênese',
  'Air-Earth-Fire-Shadow-Water': 'Cataclismo',
  'Air-Earth-Life-Shadow-Water': 'Mundo Oculto',
  'Air-Fire-Life-Shadow-Water': 'Éter Flamejante',
  'Earth-Fire-Life-Shadow-Water': 'Núcleo do Mundo',
  // 6 tipos (1)
  'Air-Earth-Fire-Life-Shadow-Water': 'Omniquimera',
};

const EN: Record<string, string> = {
  'Air': 'Air',
  'Earth': 'Earth',
  'Fire': 'Fire',
  'Life': 'Life',
  'Shadow': 'Shadow',
  'Water': 'Water',
  'Air-Earth': 'Dust',
  'Air-Fire': 'Lightning',
  'Air-Life': 'Pollen',
  'Air-Shadow': 'Mist',
  'Air-Water': 'Ice',
  'Earth-Fire': 'Magma',
  'Earth-Life': 'Forest',
  'Earth-Shadow': 'Cavern',
  'Earth-Water': 'Mud',
  'Fire-Life': 'Phoenix',
  'Fire-Shadow': 'Eclipse',
  'Fire-Water': 'Steam',
  'Life-Shadow': 'Ghost',
  'Life-Water': 'Marsh',
  'Shadow-Water': 'Abyss',
  'Air-Earth-Fire': 'Sky Volcano',
  'Air-Earth-Life': 'Floating Garden',
  'Air-Earth-Shadow': 'Sandstorm',
  'Air-Earth-Water': 'Deluge',
  'Air-Fire-Life': 'Aurora',
  'Air-Fire-Shadow': 'Black Smoke',
  'Air-Fire-Water': 'Cyclone',
  'Air-Life-Shadow': 'Mirage',
  'Air-Life-Water': 'Dewdrop',
  'Air-Shadow-Water': 'Deep Fog',
  'Earth-Fire-Life': 'Living Eruption',
  'Earth-Fire-Shadow': 'Obsidian',
  'Earth-Fire-Water': 'Geyser',
  'Earth-Life-Shadow': 'Ancient Ruin',
  'Earth-Life-Water': 'Mangrove',
  'Earth-Shadow-Water': 'Submerged Grotto',
  'Fire-Life-Shadow': 'Will-o-the-Wisp',
  'Fire-Life-Water': 'Hot Spring',
  'Fire-Shadow-Water': 'Black Tide',
  'Life-Shadow-Water': 'Shadow Lake',
  'Air-Earth-Fire-Life': 'Volcanic Paradise',
  'Air-Earth-Fire-Shadow': 'Wind Ash',
  'Air-Earth-Fire-Water': 'Primordial Storm',
  'Air-Earth-Life-Shadow': 'Phantom Jungle',
  'Air-Earth-Life-Water': 'Oasis',
  'Air-Earth-Shadow-Water': 'Windy Abyss',
  'Air-Fire-Life-Shadow': 'Spectral Comet',
  'Air-Fire-Life-Water': 'Rainbow',
  'Air-Fire-Shadow-Water': 'Shadow Tempest',
  'Air-Life-Shadow-Water': 'Enchanted Mist',
  'Earth-Fire-Life-Shadow': 'Ancestral Forge',
  'Earth-Fire-Life-Water': 'Burning Spring',
  'Earth-Fire-Shadow-Water': 'Deep Cauldron',
  'Earth-Life-Shadow-Water': 'Haunted Marsh',
  'Fire-Life-Shadow-Water': 'Pure Alchemy',
  'Air-Earth-Fire-Life-Shadow': 'Elemental Chaos',
  'Air-Earth-Fire-Life-Water': 'Genesis',
  'Air-Earth-Fire-Shadow-Water': 'Cataclysm',
  'Air-Earth-Life-Shadow-Water': 'Hidden World',
  'Air-Fire-Life-Shadow-Water': 'Flaming Aether',
  'Earth-Fire-Life-Shadow-Water': 'World Core',
  'Air-Earth-Fire-Life-Shadow-Water': 'Omnichimera',
};

export function getTypeName(signature: string, locale: Locale): string {
  const dict = locale === 'en' ? EN : PT_BR;
  return dict[signature] ?? signature;
}
